import { useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import {
  getListPlatesQueryKey,
  useAddPlate,
  useDeletePlate,
  useListPlates,
} from '@api/generated'
import { PageSpinner } from '@components/PageSpinner'
import { errorMessage } from '@utils/errors'
import { PLATE_PREFIXES, PLATE_PREFIX_SET } from '@utils/plateCodes'

export const Route = createFileRoute('/plates')({ component: Plates })

function Plates() {
  const queryClient = useQueryClient()
  const plates = useListPlates()
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListPlatesQueryKey() })
  const add = useAddPlate({ mutation: { onSuccess: refresh } })
  const del = useDeletePlate({ mutation: { onSuccess: refresh } })
  const form = useForm({
    defaultValues: { prefix: '', letters: '', digits: '', name: '' },
    onSubmit: async ({ value, formApi }) => {
      try {
        await add.mutateAsync({
          data: {
            plate: `${value.prefix}-${value.letters}${value.digits}`,
            name: value.name,
            country: 'de',
          },
        })
        formApi.reset()
      } catch {
        /* shown below via add.error */
      }
    },
  })

  return (
    <div className="px-4 sm:px-8 space-y-4 sm:space-y-6 max-w-3xl">
      <h1 className="text-2xl sm:text-3xl font-bold">Meine Kennzeichen</h1>
      <p className="opacity-70 text-sm">
        Registriere die Kennzeichen deiner Fahrzeuge. Bei der Buchung des
        Parkplatzes wählst du eines davon aus. Jedes Kennzeichen kann nur einer
        Person gehören.
      </p>

      {plates.isPending && <PageSpinner inline />}
      {plates.isError && (
        <div role="alert" className="alert alert-error">
          {errorMessage(plates.error)}
        </div>
      )}
      {plates.data && (
        <div className="overflow-x-auto rounded-box bg-base-200">
          <table className="table table-sm sm:table-md">
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Name</th>
                <th>Land</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plates.data.map((p) => (
                <tr key={p.id}>
                  <td className="font-bold tracking-wide">{p.plate}</td>
                  <td>{p.name}</td>
                  <td className="uppercase">{p.country}</td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-sm btn-error btn-outline"
                      disabled={del.isPending}
                      onClick={() => {
                        const warning =
                          p.upcomingBookings > 0
                            ? `\n\nAchtung: ${p.upcomingBookings === 1 ? 'Eine kommende Buchung wird' : `${p.upcomingBookings} kommende Buchungen werden`} ebenfalls storniert.`
                            : ''
                        if (confirm(`${p.plate} löschen?${warning}`)) {
                          del.mutate({ id: p.id })
                        }
                      }}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {plates.data.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center opacity-60">
                    Noch keine Kennzeichen
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {del.isError && (
        <div role="alert" className="alert alert-error alert-soft">
          {errorMessage(del.error)}
        </div>
      )}

      <form
        className="card bg-base-200 card-body gap-4 p-4 sm:p-6"
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
      >
        <h2 className="card-title">Kennzeichen hinzufügen</h2>
        <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr] gap-3 sm:gap-4">
          <form.Field
            name="prefix"
            validators={{
              onChange: ({ value }) =>
                value && !PLATE_PREFIX_SET.has(value)
                  ? 'Unbekanntes Unterscheidungszeichen'
                  : undefined,
            }}
          >
            {(f) => (
              <label className="form-control col-span-2 sm:col-span-1">
                <input
                  className={`input input-bordered w-full uppercase ${f.state.meta.errors.length ? 'input-error' : ''}`}
                  placeholder="Ort, z. B. M"
                  list="plate-prefixes"
                  autoComplete="off"
                  required
                  maxLength={3}
                  value={f.state.value}
                  onChange={(e) =>
                    f.handleChange(e.target.value.trim().toUpperCase())
                  }
                />
                <datalist id="plate-prefixes">
                  {PLATE_PREFIXES.filter(([code]) =>
                    code.startsWith(f.state.value),
                  ).map(([code, place]) => (
                    <option key={code} value={code}>
                      {place}
                    </option>
                  ))}
                </datalist>
                <span className="label-text-alt text-error min-h-4">
                  {f.state.meta.errors[0]}
                </span>
              </label>
            )}
          </form.Field>
          <form.Field name="letters">
            {(f) => (
              <input
                className="input input-bordered w-full uppercase self-start"
                placeholder="AB"
                required
                pattern="[A-Za-z]{1,2}"
                title="1–2 Buchstaben"
                maxLength={2}
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value.toUpperCase())}
              />
            )}
          </form.Field>
          <form.Field name="digits">
            {(f) => (
              <input
                className="input input-bordered w-full uppercase self-start"
                placeholder="1234"
                required
                inputMode="numeric"
                pattern="[1-9][0-9]{0,3}[EHeh]?"
                title="1–4 Ziffern, optional E oder H"
                maxLength={5}
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value.toUpperCase())}
              />
            )}
          </form.Field>
        </div>
        <form.Field name="name">
          {(f) => (
            <input
              className="input input-bordered w-full"
              placeholder="Name (optional)"
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value)}
            />
          )}
        </form.Field>
        {add.isError && (
          <div role="alert" className="alert alert-error alert-soft text-sm">
            {errorMessage(add.error)}
          </div>
        )}
        <div>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={add.isPending || !form.state.canSubmit}
          >
            {add.isPending && <span className="loading loading-spinner" />}
            Hinzufügen
          </button>
        </div>
      </form>
    </div>
  )
}
